import { chainsMetadata } from "data/rpc";
import { useCopyToClipboard } from "logic/hooks";
import { cn, DEFAULT_DECIMALS, scaleUp, truncateAddress } from "logic/utils";
import { type ERC20Metadata } from "logic/utils/constant/tokens";
import { useMemo, type ReactNode } from "react";
import { AmountTooltip, Skeleton } from "ui/components";
import { Address } from "viem";

interface BalanceCardProps {
  title: string;
  address: Address;
  tokens: { token: ERC20Metadata; amount: bigint | null | undefined }[];
  balanceLabel?: ReactNode;
  button?: ReactNode;
  hideAddress?: boolean;
  subTitle?: string;
  className?: string;
}

export function BalanceCard(props: BalanceCardProps) {
  const { title, address, tokens, button, hideAddress, subTitle, balanceLabel, className } = props;

  const tokensBalance = useMemo(() => {
    let balance = 0n;
    for (const { token, amount } of tokens) {
      if (amount === null || amount === undefined) return amount;
      balance += scaleUp(amount, token.decimals);
    }
    return balance;
  }, [tokens]);

  return (
    <div className={cn("flex flex-col border border-outline-major max-md:flex-1", className)}>
      <div className="flex items-center gap-3 bg-bg-primary border-b border-outline-minor px-3.5 py-1">
        <span className="text-xs font-medium whitespace-nowrap">{title}</span>
        {subTitle ? (
          <span className="text-xs text-text-disabled">{subTitle}</span>
        ) : (
          <AddressWithCopy address={address} hideAddress={hideAddress} />
        )}
      </div>
      <div className="flex-1 flex items-center gap-4 px-3.5 py-1">
        <div className="flex items-center gap-1 text-text-dark-secondary">
          {balanceLabel}
          <TokenAmount amount={tokensBalance} decimals={DEFAULT_DECIMALS} />
        </div>
        {button}
      </div>
    </div>
  );
}

export function TokenBalance({ token, amount }: { token: ERC20Metadata; amount: bigint | null | undefined }) {
  const chainMeta = chainsMetadata[token.chainId];

  return (
    <div className="flex items-center gap-1 text-text-dark-secondary">
      <span className="text-xs font-medium">{token.symbol}</span>
      <img src={chainMeta?.logoUrl} alt={chainMeta?.name} title={chainMeta?.name} className="shrink-0 size-4" />
      <TokenAmount amount={amount} decimals={token.decimals} />
    </div>
  );
}

function TokenAmount({ amount, decimals }: { amount: bigint | null | undefined; decimals: number }) {
  if (amount === undefined) return <Skeleton className="w-16 h-3" />;
  if (amount === null) return <span className="text-xs">--</span>;
  return <AmountTooltip className="text-xs" amount={amount} decimals={decimals} />;
}

function AddressWithCopy({ address, hideAddress = false }: { address: Address; hideAddress?: boolean }) {
  const { copy, CopyIcon } = useCopyToClipboard();

  return (
    <button type="button" onClick={() => copy(address)} className="flex items-center gap-1 outline-none">
      {!hideAddress ? <span className="text-xs text-text-dark-secondary">{truncateAddress(address)}</span> : null}
      <CopyIcon className="size-3 shrink-0 text-text-disabled" />
    </button>
  );
}
